<template>
  <div
    class="youtube-player"
    :class="fill ? 'tw-relative tw-h-full tw-w-full tw-overflow-hidden' : 'tw-relative tw-w-full tw-overflow-hidden'"
    :style="ratioStyle"
  >
    <div ref="mount" class="tw-absolute tw-inset-0 tw-h-full tw-w-full" />
    <div
      v-if="errorMessage"
      class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-p-4 tw-text-center tw-text-sm tw-text-white"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { extractYoutubeVideoId, loadYoutubeIframeApi } from '@/utils/youtube'

// https://developers.google.com/youtube/iframe_api_reference#onError
const ERROR_MESSAGES = {
  2: 'El enlace de YouTube no es válido.',
  5: 'Este video no se puede reproducir en este navegador.',
  100: 'El video no está disponible o fue eliminado.',
  101: 'El propietario del video no permite reproducirlo fuera de YouTube.',
  150: 'El propietario del video no permite reproducirlo fuera de YouTube.',
}

export default {
  name: 'YoutubePlayer',
  props: {
    src: {
      type: String,
      default: '',
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    // true when the parent needs the player to fill its container height
    // (e.g. side-by-side with chat); false uses the aspectRatio box instead.
    fill: {
      type: Boolean,
      default: false,
    },
    aspectRatio: {
      type: String,
      default: '16:9',
    },
  },
  data: () => ({
    player: null,
    errorMessage: '',
  }),
  computed: {
    ratioStyle() {
      if (this.fill) {
        return {}
      }
      const [w, h] = this.aspectRatio.split(':').map(Number)
      const ratio = w && h ? (h / w) * 100 : 56.25
      return { height: 0, paddingBottom: `${ratio}%` }
    },
  },
  watch: {
    src() {
      this.initPlayer()
    },
  },
  mounted() {
    this.initPlayer()
  },
  beforeDestroy() {
    this.destroyPlayer()
  },
  methods: {
    async initPlayer() {
      this.destroyPlayer()
      this.errorMessage = ''
      const videoId = extractYoutubeVideoId(this.src)
      if (!videoId) {
        this.errorMessage = 'No se pudo identificar el video de YouTube.'
        return
      }
      try {
        const YT = await loadYoutubeIframeApi()
        // component may have been destroyed while the API script was loading
        if (!this.$refs.mount) {
          return
        }
        this.player = new YT.Player(this.$refs.mount, {
          videoId,
          playerVars: {
            autoplay: this.autoplay ? 1 : 0,
            playsinline: 1,
            rel: 0,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onError: this.handlePlayerError,
          },
        })
      } catch (e) {
        this.errorMessage = 'No se pudo cargar el reproductor de YouTube.'
      }
    },
    handlePlayerError(event) {
      this.errorMessage =
        ERROR_MESSAGES[event.data] || 'Ocurrió un error al reproducir el video.'
    },
    pauseVideo() {
      if (this.player && typeof this.player.pauseVideo === 'function') {
        try {
          this.player.pauseVideo()
        } catch (e) {
          // player iframe may already be gone
        }
      }
    },
    destroyPlayer() {
      if (this.player && typeof this.player.destroy === 'function') {
        try {
          this.player.destroy()
        } catch (e) {
          // player iframe may already be gone
        }
      }
      this.player = null
    },
  },
}
</script>
